import { useState, useEffect, useRef } from 'react';
import { NavLink } from 'react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { KpiCard } from '@/components/KpiCard';
import {
  MessageSquare, Users, TrendingUp, ThumbsUp, BookOpen,
  AlertTriangle, ArrowDownRight, Minus, ThumbsDown,
  Zap, ChevronRight,
  Trophy, Flame, Award, CheckCircle, Send, Play
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api, { BROWSER_TZ } from '@/lib/api';
import { getTopicIconComponent } from '@/lib/topicIcons';
import {
  chartTooltipStyle,
  commonCartesianGridProps,
  commonLegendProps,
  commonXAxisProps,
  commonYAxisProps,
  getTrendXAxisInterval,
} from '@/lib/chartTheme';
import { useAuth } from '@/contexts/AuthContext';
import { EvaPeopleActivityTicker } from '@/components/LiveBroadcast';
import { WelcomeGraphic, welcomeConfigs, useWelcomeGraphic } from '@/components/WelcomeGraphic';
import { usePageTour } from '@/components/PageTour';
import { TrendRangeTabs, getTrendRangeLabel } from '@/components/TrendRangeTabs';
import { RegionalMapView } from '@/components/RegionalMapView';
import { AnalyticsTabBar } from '@/components/AnalyticsTabBar';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';

const TOP_FIVE_SLOTS = Array.from({ length: 5 }, (_, idx) => idx);
const EVA_LOGO_URL = '/eva-icon-square.png';
const PULSE_GOAL_STORAGE_KEY = 'eva-pulse-optimization-goal';
const PULSE_MESSAGES_STORAGE_KEY = 'eva-pulse-conversation';
const PULSE_GOALS = [
  { key: 'repetitive_questions', label: 'Answer repetitive questions', categories: ['Knowledge Base', 'Conversations'] },
  { key: 'self_service', label: 'Improve employee self-service', categories: ['People', 'Engagement'] },
  { key: 'policy_accuracy', label: 'Keep policies accurate', categories: ['Knowledge Base'] },
  { key: 'workforce_needs', label: 'Understand workforce needs', categories: ['Conversations', 'People'] },
  { key: 'operations', label: 'Keep Eva running smoothly', categories: ['Connectors', 'Billing', 'AI Usage', 'Operations'] },
];
function formatCompactNumber(value) {
  const number = Number(value || 0);
  if (number >= 1000000) return `${(number / 1000000).toFixed(1)}M`;
  if (number >= 1000) return `${(number / 1000).toFixed(1)}K`;
  return number.toLocaleString();
}

function formatTokenCount(value) {
  const number = Number(value || 0);
  if (!number) return '0';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: number >= 1000000 ? 2 : 1,
  }).format(number);
}

function formatTokenCapacityDescription(alert) {
  const usage = Number(alert.usage ?? alert.tokens_used ?? 0);
  const limit = Number(alert.limit ?? alert.tokens_limit ?? 0);
  const percent = Number(alert.maxPercent ?? alert.percent_used ?? alert.current_usage_pct ?? 0);

  if (usage > 0 && limit > 0) {
    const percentLabel = percent > 0 ? ` • ${percent.toFixed(1)}%` : '';
    return `${formatTokenCount(usage)} of ${formatTokenCount(limit)} credits used${percentLabel}`;
  }

  if (percent > 0) {
    return `Monthly AI usage is at ${percent.toFixed(1)}% of your credit limit`;
  }

  return alert.description || 'AI usage needs attention';
}

function formatTrendBucketLabel(bucket, windowLabel, fallback) {
  if (!bucket) return fallback;
  const date = new Date(bucket);
  if (Number.isNaN(date.getTime())) return fallback;
  if (windowLabel === 'daily') {
    return date.toLocaleTimeString(undefined, { hour: 'numeric' });
  }
  if (windowLabel === 'weekly') {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }
  return date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' });
}

const TOPIC_SERIES_COLORS = [
  '#6366f1',
  '#10b981',
  '#3b82f6',
  '#f59e0b',
  '#ef4444',
];

function getPulseTitle(item) {
  if (item.type === 'approval' && item.resource === 'documents') return 'Approve ready documents';
  if (item.resource === 'tokens') return 'Manage AI credits';
  if (item.type === 'subscription' && item.resource === 'trial') return 'Choose your next plan';
  // EIP-318: the backend now names the actual event (e.g. "Subscription ending",
  // "Subscription suspended") instead of one generic message — this override used
  // to erase that server-authored title for every subscription-status card, which
  // made the backend copy change a silent no-op.
  if (item.type === 'subscription' && item.resource === 'subscription') return item.title;
  if (item.type === 'subscription') return 'Review subscription access';
  if (item.type === 'limit') return `Adjust ${String(item.area || item.resource || 'capacity').toLowerCase()}`;
  return item.title;
}

function getPulseCategory(item) {
  if (item.category) return item.category === 'Token Usage' ? 'AI Usage' : item.category;
  if (item.area === 'Knowledge Base') return 'Knowledge Base';
  if (item.area === 'People') return 'People';
  if (item.area === 'Token Usage') return 'AI Usage';
  if (item.area === 'Billing') return 'Billing';
  if (item.resource === 'tokens') return 'AI Usage';
  if (item.resource === 'trial' || item.resource === 'subscription') return 'Billing';
  return item.area || 'Operations';
}

function getPulseGoal() {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(PULSE_GOAL_STORAGE_KEY) || '';
}

function getPulseMessagesStorageKey(user, tenant) {
  const tenantId = tenant?.tenant_id || tenant?.id || user?.tenant_id || 'workspace';
  const userId = user?.id || user?.email || user?.user_id || 'user';
  return `${PULSE_MESSAGES_STORAGE_KEY}:${tenantId}:${userId}`;
}

function readStoredPulseMessages(storageKey) {
  if (typeof window === 'undefined' || !storageKey) return [];

  try {
    const stored = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
    if (!Array.isArray(stored)) return [];

    return stored
      .filter((message) => message?.id && message?.role && typeof message.text === 'string')
      .slice(-24)
      .map((message) => ({ ...message, streaming: false }));
  } catch (_error) {
    return [];
  }
}

function sortPulseAlerts(alerts, selectedGoal) {
  const selected = PULSE_GOALS.find((goal) => goal.key === selectedGoal);
  const preferredCategories = selected?.categories || [];
  const severityRank = { blocked: 0, critical: 1, high: 2, warning: 3, medium: 4, low: 5 };

  return [...alerts].sort((a, b) => {
    const aPreferred = preferredCategories.includes(a.category) ? 0 : 1;
    const bPreferred = preferredCategories.includes(b.category) ? 0 : 1;
    if (aPreferred !== bPreferred) return aPreferred - bPreferred;
    return (severityRank[a.severity] ?? 9) - (severityRank[b.severity] ?? 9);
  });
}

function getPulseSeverityRank(severity) {
  const severityRank = { blocked: 0, critical: 1, high: 2, warning: 3, medium: 4, low: 5 };
  return severityRank[severity] ?? 9;
}

function getPulseAlertPercent(alert) {
  if (alert.percent_used) return Number(alert.percent_used);
  if (alert.current_usage_pct) return Number(alert.current_usage_pct);
  const match = String(alert.description || '').match(/([\d.]+)%/);
  return match ? Number(match[1]) : 0;
}

function groupPulseAlerts(alerts) {
  const grouped = new Map();

  alerts.forEach((alert) => {
    const isTokenCapacity = alert.resource === 'tokens' || alert.category === 'AI Usage' || alert.title === 'Manage AI credits';
    const key = isTokenCapacity
      ? 'token-capacity'
      : `${alert.title || 'signal'}|${alert.category || 'operations'}|${alert.link || ''}`;
    const percent = getPulseAlertPercent(alert);
    const existing = grouped.get(key);

    if (!existing) {
      grouped.set(key, {
        ...alert,
        signalCount: 1,
        maxPercent: percent,
      });
      return;
    }

    const existingRank = getPulseSeverityRank(existing.severity);
    const currentRank = getPulseSeverityRank(alert.severity);
    const shouldPromote = currentRank < existingRank || percent > existing.maxPercent;
    const winner = shouldPromote ? alert : existing;
    const count = existing.signalCount + 1;
    const maxPercent = Math.max(existing.maxPercent || 0, percent);
    const description = isTokenCapacity
      ? formatTokenCapacityDescription({ ...winner, maxPercent })
      : winner.description;

    grouped.set(key, {
      ...winner,
      description,
      signalCount: count,
      maxPercent,
    });
  });

  return [...grouped.values()];
}

function summarizePulseContext(context = {}) {
  const tenantName = context.tenant?.display_name || context.tenant?.name || context.tenant?.shortname || 'this workspace';
  const tenantShortName = context.tenant?.shortname || context.tenant?.slug || context.tenant?.tenant_id || context.user?.tenant_id || '';
  const role = context.user?.platform_role || 'your role';
  const alertCount = context.alerts?.length || 0;
  const topSignals = (context.alerts || [])
    .slice(0, 3)
    .map((alert) => `${alert.title}${alert.category ? ` (${alert.category})` : ''}`)
    .join(', ');

  return {
    tenantName,
    tenantShortName,
    role,
    alertCount,
    topSignals,
  };
}

function buildPulseGoalReply(goal, recommendation, context = {}) {
  const nextStep = recommendation
    ? `Start with ${recommendation.title.toLowerCase()}. It is the clearest signal to move on first.`
    : 'I’ll bring forward the clearest next step when a strong signal appears.';
  const summary = summarizePulseContext(context);
  const signalContext = summary.alertCount > 0
    ? `${summary.alertCount} current ${summary.alertCount === 1 ? 'signal points' : 'signals point'} to where Eva can help next.`
    : `${summary.tenantName} looks calm right now.`;

  const replies = {
    repetitive_questions: `${signalContext} To reduce repetitive HR questions, focus on content that is ready to approve, gaps employees are hitting, and topics with repeated demand. ${nextStep}`,
    self_service: `${signalContext} To improve employee self-service, focus on adoption, inactive groups, unanswered needs, and nudges that help employees try Eva. ${nextStep}`,
    policy_accuracy: `${signalContext} To keep policies accurate, focus on approvals, stale content, coverage gaps, and feedback that suggests employees are not finding the right answer. ${nextStep}`,
    workforce_needs: `${signalContext} To understand workforce needs, focus on rising topics, feedback patterns, regional signals, and repeated question themes. ${nextStep}`,
    operations: `${signalContext} To keep Eva running smoothly, focus on connectors, limits, credit usage, sync health, and subscription readiness. ${nextStep}`,
  };

  return replies[goal?.key] || `${signalContext} ${nextStep}`;
}

function buildPulseFollowUpReply(message, goal, recommendation, context = {}) {
  const trimmed = message.trim();
  if (!trimmed) return '';

  const summary = summarizePulseContext(context);
  const normalized = trimmed.toLowerCase();
  const asksAboutOrg = /\b(org|organization|tenant|company|workspace)\b/.test(normalized)
    && /\b(what|which|who|where|my|current)\b/.test(normalized);
  const asksAboutRole = /\b(role|permission|access)\b/.test(normalized)
    && /\b(what|which|who|my|current)\b/.test(normalized);

  if (asksAboutOrg) {
    return `You’re viewing ${summary.tenantName}${summary.tenantShortName ? ` (${summary.tenantShortName})` : ''}. Your current role is ${summary.role}.`;
  }

  if (asksAboutRole) {
    return `Your current role is ${summary.role}${summary.tenantName ? ` in ${summary.tenantName}` : ''}.`;
  }

  const objective = goal?.label?.toLowerCase() || 'your HR objective';
  const nextStep = recommendation
    ? `Start with ${recommendation.title.toLowerCase()}. That is the strongest current move for ${objective}.`
    : `I do not see an urgent action yet. I’ll keep watching for the first signal that clearly supports ${objective}.`;
  const supportingSignals = summary.topSignals
    ? `After that, keep an eye on ${summary.topSignals}.`
    : '';

  return `${nextStep} ${supportingSignals}`.trim();
}

function buildPulseAlertReply(alert, goal, context = {}) {
  const summary = summarizePulseContext(context);
  const goalContext = goal
    ? `Since you’re optimizing for ${goal.label.toLowerCase()}, `
    : '';
  const categoryContext = alert.category ? `This is coming from ${alert.category}. ` : '';
  const action = alert.ctaLabel || 'Open';
  const usageContext = alert.percent_used
    ? ` It is at ${Number(alert.percent_used).toFixed(1)}% of the configured limit.`
    : alert.usage && alert.limit
      ? ` Current usage is ${Number(alert.usage).toLocaleString()} of ${Number(alert.limit).toLocaleString()}.`
      : '';

  return `${goalContext}${categoryContext}${alert.description}.${usageContext} Handle “${alert.title}” first if you want the fastest visible improvement. ${summary.alertCount > 1 ? `There are ${summary.alertCount - 1} other ${summary.alertCount - 1 === 1 ? 'signal' : 'signals'} behind it, but this one is the clearest next step.` : ''} Use “${action}” when you’re ready.`;
}

function getPulseSeverityConfig(severity) {
  const severityConfig = {
    blocked: {
      label: 'Do now',
      icon: AlertTriangle,
      card: 'border-destructive/25 bg-destructive/5',
      iconClass: 'bg-destructive/10 text-destructive',
      badge: 'border-destructive/25 bg-destructive/10 text-destructive',
    },
    critical: {
      label: 'Do next',
      icon: AlertTriangle,
      card: 'border-orange-300/50 bg-orange-500/10',
      iconClass: 'bg-orange-500/15 text-orange-600',
      badge: 'border-orange-300/60 bg-orange-500/15 text-orange-700',
    },
    high: {
      label: 'Recommended',
      icon: Zap,
      card: 'border-destructive/20 bg-destructive/5',
      iconClass: 'bg-destructive/10 text-destructive',
      badge: 'border-destructive/25 bg-destructive/10 text-destructive',
    },
    warning: {
      label: 'Worth doing',
      icon: AlertTriangle,
      card: 'border-warning/30 bg-warning/5',
      iconClass: 'bg-warning/15 text-warning',
      badge: 'border-warning/30 bg-warning/10 text-warning',
    },
    medium: {
      label: 'Review',
      icon: AlertTriangle,
      card: 'border-warning/25 bg-warning/5',
      iconClass: 'bg-warning/15 text-warning',
      badge: 'border-warning/25 bg-warning/10 text-warning',
    },
    low: {
      label: 'Insight',
      icon: CheckCircle,
      card: 'border-info/20 bg-info/5',
      iconClass: 'bg-info/10 text-info',
      badge: 'border-info/25 bg-info/10 text-info',
    },
  };

  return severityConfig[severity] || severityConfig.medium;
}

function PulseSignalCard({ alert, index, onAsk, featured = false }) {
  const config = getPulseSeverityConfig(alert.severity);
  const Icon = config.icon;
  const categoryLabel = alert.category === 'Conversations' ? 'Conversation insights' : alert.category;
  const actionLabel = alert.ctaLabel || 'Open';

  return (
    <div
      className={`group relative animate-fade-in overflow-hidden rounded-2xl border bg-card/80 p-3 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-md ${
        featured ? 'border-primary/25 shadow-sm' : 'border-border/75'
      }`}
      style={{ animationDelay: `${index * 90}ms` }}
    >
      <span
        className={`absolute left-0 top-3 h-10 w-1 rounded-r-full ${
          alert.severity === 'blocked' || alert.severity === 'critical'
            ? 'bg-orange-500'
            : alert.severity === 'high'
              ? 'bg-destructive'
              : alert.severity === 'low'
                ? 'bg-info'
                : 'bg-warning'
        }`}
        aria-hidden="true"
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 gap-3">
          <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${config.iconClass}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-sm font-semibold text-foreground">{alert.title}</p>
              {categoryLabel && (
                <Badge variant="outline" className="border-border/70 bg-background/60 text-xs font-medium text-muted-foreground">
                  {categoryLabel}
                </Badge>
              )}
              {alert.signalCount > 1 && (
                <Badge variant="outline" className="border-primary/20 bg-primary/5 text-xs text-primary">
                  {alert.signalCount} signals
                </Badge>
              )}
              {featured && (
                <Badge className={`border text-xs ${config.badge}`}>{config.label}</Badge>
              )}
            </div>
            <p className="mt-1 text-sm leading-5 text-muted-foreground">{alert.description}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:pt-0.5">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 rounded-xl px-2 text-muted-foreground hover:text-primary"
            onClick={() => onAsk(alert)}
          >
            Ask Eva
          </Button>
          <NavLink to={alert.link || '/'}>
            {featured ? (
              <Button size="sm" className="h-8 rounded-xl px-3">
                {actionLabel}
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            ) : (
              <span className="inline-flex h-8 items-center rounded-xl px-2 text-sm font-medium text-primary transition-colors hover:bg-primary/10">
                {actionLabel}
                <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            )}
          </NavLink>
        </div>
      </div>
    </div>
  );
}

function ActionListCard({ alerts }) {
  const visibleAlerts = alerts.slice(0, 5);

  return (
    <Card className="card-elevated border-0" data-testid="overview-alerts-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-xl font-semibold text-foreground">
          <AlertTriangle className="h-5 w-5 text-primary" />
          Live Signals
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Eva updates this list from current data. Items clear automatically when the underlying issue is resolved.
        </p>
      </CardHeader>
      <CardContent>
        {visibleAlerts.length > 0 ? (
          <div className="space-y-3">
            {visibleAlerts.map((alert, index) => {
              const config = getPulseSeverityConfig(alert.severity);
              const Icon = config.icon;
              const actionLabel = alert.ctaLabel || 'Open';

              return (
                <div
                  key={`${alert.title}-${index}`}
                  className="flex flex-col gap-3 rounded-2xl border border-border bg-background/60 p-4 transition-colors hover:border-primary/25 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div className="flex min-w-0 gap-3">
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${config.iconClass}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-foreground">{alert.title}</p>
                        {alert.category && (
                          <Badge variant="outline" className="border-border bg-card text-xs text-muted-foreground">
                            {alert.category}
                          </Badge>
                        )}
                        <Badge className={`border text-xs ${config.badge}`}>{config.label}</Badge>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{alert.description}</p>
                    </div>
                  </div>
                  <NavLink to={alert.link || '/'}>
                    <Button variant="outline" size="sm" className="h-9 rounded-xl">
                      {actionLabel}
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </NavLink>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex min-h-[160px] items-center justify-center rounded-2xl border border-dashed border-border bg-secondary/30 px-4 py-8 text-center">
            <div>
              <CheckCircle className="mx-auto mb-3 h-8 w-8 text-success" />
              <p className="font-semibold text-foreground">No live signals right now</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Documents, limits, feedback, and connector signals will appear here when the workspace needs a next step.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PulseMessage({ message }) {
  return (
    <div
      className={`animate-fade-in flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
    >
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-6 shadow-sm transition-all duration-300 ${
          message.role === 'user'
            ? 'rounded-br-sm bg-primary text-primary-foreground'
            : 'rounded-tl-sm border border-border bg-card text-muted-foreground'
        }`}
      >
        {message.text}
        {message.streaming && (
          <span className="ml-1 inline-flex align-middle">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
          </span>
        )}
        {(message.recommendation || message.cta) && !message.streaming && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {(message.recommendation || message.cta) && (
              <NavLink
                to={message.recommendation.link || message.cta?.link || '/'}
                className="inline-flex max-w-full items-center rounded-full border border-primary/20 bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary transition-colors hover:bg-primary/15"
              >
                Next step
                <span className="ml-2 max-w-[260px] truncate text-foreground">
                  {message.recommendation?.title || message.cta?.label}
                </span>
                <span className="ml-2 inline-flex items-center text-muted-foreground">
                  {message.cta?.label || 'Open'}
                  <ChevronRight className="ml-1 h-3.5 w-3.5" />
                </span>
              </NavLink>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function PulseGoalAnchor({ goal, onChange }) {
  if (!goal) return null;

  return (
    <div className="animate-fade-in rounded-2xl border border-primary/15 bg-primary/5 px-4 py-3 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Eva is optimizing for</p>
          <p className="mt-1 truncate text-sm font-semibold text-foreground">{goal.label}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 rounded-xl px-3"
          onClick={onChange}
        >
          Change goal
        </Button>
      </div>
    </div>
  );
}

function EvaSpeakingAvatar({ active }) {
  return (
    <div className="relative flex h-16 w-16 shrink-0 items-center justify-center">
      {active && (
        <>
          <span className="absolute inset-0 rounded-3xl bg-primary/20 animate-ping" aria-hidden="true" />
          <span className="absolute inset-1 rounded-3xl bg-accent/10 animate-pulse" aria-hidden="true" />
        </>
      )}
      <div className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-accent/15 shadow-sm">
        <img src={EVA_LOGO_URL} alt="Eva" className="h-10 w-10 rounded-xl" />
      </div>
      {active && (
        <div className="absolute -bottom-1 left-1/2 flex -translate-x-1/2 items-end gap-0.5 rounded-full border border-border bg-background px-2 py-1 shadow-sm" aria-hidden="true">
          {[0, 1, 2].map((bar) => (
            <span
              key={bar}
              className="h-2 w-1 rounded-full bg-primary animate-pulse"
              style={{ animationDelay: `${bar * 160}ms` }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UsageLeadersCard({ leaders }) {
  const [selectedWindow, setSelectedWindow] = useState('dau');
  const windows = [
    { key: 'dau', label: 'Daily', fullLabel: 'Daily Active Users', acronym: 'DAU', caption: 'Today' },
    { key: 'wau', label: 'Weekly', fullLabel: 'Weekly Active Users', acronym: 'WAU', caption: 'This week' },
    { key: 'mau', label: 'Monthly', fullLabel: 'Monthly Active Users', acronym: 'MAU', caption: 'This billing period' },
  ];
  const activeWindow = windows.find((window) => window.key === selectedWindow) || windows[0];
  const activeLeaders = leaders[activeWindow.key] || [];
  const rows = Array.isArray(activeLeaders) ? activeLeaders : activeLeaders.rows || [];
  const activeCount = Array.isArray(activeLeaders)
    ? rows.length
    : Number(activeLeaders.count ?? rows.length);

  return (
    <Card className="card-elevated border-0" data-testid="overview-usage-leaders-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div
            title="Ranked by Eva People chat activity only — the same conversation credits shown on each employee's own Eva People Credits meter. Eva Insights knowledge-base work (document uploads, analysis) is tracked separately on Settings → Usage."
          >
            <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Usage Leaders
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Eva People usage leaders for {activeWindow.caption.toLowerCase()}
            </p>
          </div>
          <div className="grid grid-cols-3 rounded-xl bg-secondary p-1" aria-label="Usage leader time range">
            {windows.map((window) => (
              <button
                key={window.key}
                type="button"
                onClick={() => setSelectedWindow(window.key)}
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  selectedWindow === window.key
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {window.label}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-xl border border-border bg-secondary/30 p-3">
          <div className="mb-3 flex items-baseline justify-between">
            <div>
              <p className="text-sm font-semibold text-foreground">
                {activeWindow.fullLabel}
                <span className="ml-1 text-muted-foreground">({activeWindow.acronym})</span>
              </p>
              <p className="text-xs text-muted-foreground">{activeWindow.caption}</p>
            </div>
            <Badge variant="secondary" className="border-0">{activeCount}</Badge>
          </div>
          <div className="space-y-2">
            {rows.length === 0 ? (
              <EmptyState
                icon={Users}
                title="No usage yet"
                description="Leaders will appear once employees ask Eva questions."
                variant="panel"
                compact
                className="bg-background/50"
              />
            ) : TOP_FIVE_SLOTS.map((idx) => {
              const row = rows[idx];
              return (
                <div key={`${activeWindow.key}-${row?.user_email || idx}`} className={`flex min-h-[64px] items-center justify-between gap-3 rounded-xl p-3 ${row ? 'bg-background/50' : 'bg-background/25 text-muted-foreground'}`}>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${row ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                      {idx + 1}
                    </div>
                    {row && (
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">
                          {row.user_name || row.name || row.user_email?.split('@')[0] || 'Unknown user'}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {row.user_email || ''}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-semibold text-foreground">
                      {row ? `${Number(row.conversation_request_count || 0).toLocaleString()} requests` : '-'}
                    </p>
                    {row && (
                      <p className="text-xs text-muted-foreground">
                        {formatTokenCount(row.conversation_tokens || 0)} credits
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function GamificationLeaderboardCard({ leaders }) {
  return (
    <Card className="card-elevated border-0" data-testid="overview-gamification-leaderboard-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Trophy className="w-5 h-5 text-warning" />
          Leaderboard
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {leaders.length === 0 ? (
          <EmptyState
            icon={Trophy}
            title="No leaderboard activity yet"
            description="Points and levels will appear as employees engage."
            variant="panel"
            className="border-0 bg-secondary/50"
          />
        ) : TOP_FIVE_SLOTS.map((idx) => {
          const entry = leaders[idx];
          return (
            <div key={entry?.user_id || entry?.user_email || idx} className="grid min-h-[72px] grid-cols-1 gap-3 rounded-xl bg-secondary/50 p-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
              <div className="flex min-w-0 items-center gap-3">
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${entry ? 'bg-warning/10 text-warning' : 'bg-muted text-muted-foreground'}`}>
                  {entry?.rank || idx + 1}
                </div>
                {entry && (
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">
                      {entry.name || entry.user_email?.split('@')[0] || 'Unknown user'}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {entry.user_email || ''}
                    </p>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-lg bg-background/60 px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                    <Zap className="h-3 w-3" />
                    Level
                  </div>
                  <p className="text-sm font-semibold text-foreground">{entry ? entry.level || 1 : '-'}</p>
                </div>
                <div className="rounded-lg bg-background/60 px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                    <Trophy className="h-3 w-3" />
                    Points
                  </div>
                  <p className="text-sm font-semibold text-foreground">{entry ? Number(entry.points || 0).toLocaleString() : '-'}</p>
                </div>
                <div className="rounded-lg bg-background/60 px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                    <Flame className="h-3 w-3" />
                    Streak
                  </div>
                  <p className="text-sm font-semibold text-foreground">{entry ? entry.current_streak || 0 : '-'}</p>
                </div>
                <div className="rounded-lg bg-background/60 px-3 py-2 text-center">
                  <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                    <Award className="h-3 w-3" />
                    Badges
                  </div>
                  <p className="text-sm font-semibold text-foreground">{entry ? entry.badges_count || 0 : '-'}</p>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function RecentFeedbackCard({ feedback }) {
  const formatFeedbackTime = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return 'Unknown time';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  return (
    <Card className="card-elevated border-0" data-testid="overview-recent-feedback-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
            <ThumbsUp className="w-5 h-5 text-success" />
            Recent Feedback
          </CardTitle>
          <NavLink to="/conversations/feedback" className="text-sm text-primary hover:underline">
            View all
          </NavLink>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="hidden grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-3 border-b border-border px-3 pb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid">
          <span>Rank</span>
          <span>Interaction</span>
          <span>Rating</span>
          <span className="text-right">Date</span>
        </div>
        {feedback.length === 0 ? (
          <EmptyState
            icon={ThumbsUp}
            title="No feedback yet"
            description="Recent ratings will appear here after employees respond."
            variant="panel"
          />
        ) : TOP_FIVE_SLOTS.map((idx) => {
          const item = feedback[idx];
          const isPositive = item?.rating === 'thumbs_up' || item?.feedback === 'helpful' || item?.feedback === 'positive';
          const Icon = isPositive ? ThumbsUp : ThumbsDown;
          return (
            <div key={item?.id || idx} className="grid min-h-[64px] grid-cols-1 gap-2 rounded-xl border border-border bg-card/70 p-3 sm:grid-cols-[auto_minmax(0,1fr)_auto_auto] sm:items-center sm:gap-3">
              <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${item ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                {idx + 1}
              </div>
              <div className="min-w-0">
                {item ? (
                  <>
                    <p className="truncate text-sm font-medium text-foreground">
                      {item.query || 'No query recorded'}
                    </p>
                    {item.comment && (
                      <p className="truncate text-xs text-muted-foreground">
                        {item.comment}
                      </p>
                    )}
                  </>
                ) : null}
              </div>
              <Badge className={`w-fit border-0 ${item ? (isPositive ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive') : 'bg-muted text-muted-foreground'}`}>
                {item && <Icon className="mr-1.5 h-3 w-3" />}
                {item ? (isPositive ? 'Helpful' : 'Needs review') : '-'}
              </Badge>
              <p className="text-xs text-muted-foreground sm:text-right">
                {item ? formatFeedbackTime(item.timestamp) : '-'}
              </p>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function WeeklyActivityCard({ trend, windowLabel, controls, loading = false }) {
  const days = trend;
  const total = days.reduce((sum, day) => sum + Number(day.conversations || day.count || day.value || 0), 0);
  const average = days.length > 0 ? Math.round(total / days.length) : 0;
  const peak = days.reduce((best, day) => {
    const value = Number(day.conversations || day.count || day.value || 0);
    return value > best.value ? { value, date: day.date } : best;
  }, { value: 0, date: null });

  return (
    <Card className="card-elevated border-0" data-testid="overview-weekly-usage-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-accent" />
              Usage Rhythm
            </CardTitle>
            <p className="text-sm text-muted-foreground">Questions and conversations over the {windowLabel}</p>
          </div>
          {controls}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="space-y-5" data-testid="overview-usage-trend-loading">
            <div className="grid grid-cols-3 gap-3">
              <Skeleton className="h-[74px] rounded-xl" />
              <Skeleton className="h-[74px] rounded-xl" />
              <Skeleton className="h-[74px] rounded-xl" />
            </div>
            <Skeleton className="h-56 w-full rounded-xl" />
          </div>
        ) : (
        <>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-secondary/50 p-3">
            <p className="text-xs text-muted-foreground">Period total</p>
            <p className="text-xl font-semibold text-foreground">{formatCompactNumber(total)}</p>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3">
            <p className="text-xs text-muted-foreground">{windowLabel === 'daily' ? 'Hourly avg' : 'Daily avg'}</p>
            <p className="text-xl font-semibold text-foreground">{formatCompactNumber(average)}</p>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3">
            <p className="text-xs text-muted-foreground">{windowLabel === 'daily' ? 'Peak hour' : 'Peak day'}</p>
            <p className="text-xl font-semibold text-foreground">{formatCompactNumber(peak.value)}</p>
          </div>
        </div>

        {days.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="No usage trend yet"
            description="Activity will appear after employees ask Eva questions."
            variant="panel"
            className="border-0 bg-secondary/40"
          />
        ) : (
          <div className="h-56 rounded-xl bg-secondary/30 p-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={days} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barCategoryGap="28%" maxBarSize={42}>
                <CartesianGrid {...commonCartesianGridProps} />
                <XAxis
                  dataKey="date"
                  {...commonXAxisProps}
                  interval={getTrendXAxisInterval(windowLabel)}
                  tickFormatter={(value, idx) => formatTrendBucketLabel(value, windowLabel, idx + 1)}
                />
                <YAxis {...commonYAxisProps} tickFormatter={formatCompactNumber} allowDecimals={false} width={36} />
                <Tooltip contentStyle={chartTooltipStyle} labelFormatter={(value) => formatTrendBucketLabel(value, windowLabel, value)} />
                <Bar dataKey="conversations" fill="#6366f1" name="Questions" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        </>
        )}
      </CardContent>
    </Card>
  );
}

function FeedbackMixCard({ trend, snapshot, windowLabel, controls, loading = false }) {
  const days = trend;
  const totalPositive = days.reduce((sum, day) => sum + Number(day.positive || 0), 0);
  const totalNegative = days.reduce((sum, day) => sum + Number(day.negative || 0), 0);
  const total = totalPositive + totalNegative;
  const positivePct = total > 0 ? Math.round((totalPositive / total) * 100) : Math.round(snapshot.positive_pct || 0);

  return (
    <Card className="card-elevated border-0" data-testid="overview-feedback-mix-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-success" />
              Feedback Mix
            </CardTitle>
            <p className="text-sm text-muted-foreground">Helpful vs needs-review ratings over the {windowLabel}</p>
          </div>
          {controls}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="space-y-5" data-testid="overview-feedback-trend-loading">
            <Skeleton className="h-[76px] w-full rounded-xl" />
            <Skeleton className="h-56 w-full rounded-xl" />
          </div>
        ) : (
        <>
        <div className="rounded-xl bg-secondary/40 p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Positive sentiment</span>
            <span className="text-lg font-semibold text-foreground">{positivePct}%</span>
          </div>
          <div className="h-3 overflow-hidden rounded-full bg-destructive/15">
            <div className="h-full rounded-full bg-success" style={{ width: `${positivePct}%` }} />
          </div>
        </div>

        {days.length === 0 ? (
          <EmptyState
            icon={ThumbsUp}
            title="No feedback trend yet"
            description="Feedback trends will appear after employees rate answers."
            variant="panel"
            compact
            className="border-0 bg-secondary/40"
          />
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={days} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barCategoryGap="28%" maxBarSize={42}>
                <CartesianGrid {...commonCartesianGridProps} />
                <XAxis
                  dataKey="date"
                  {...commonXAxisProps}
                  interval={getTrendXAxisInterval(windowLabel)}
                  tickFormatter={(value, idx) => formatTrendBucketLabel(value, windowLabel, idx + 1)}
                />
                <YAxis {...commonYAxisProps} tickFormatter={formatCompactNumber} allowDecimals={false} width={36} />
                <Tooltip contentStyle={chartTooltipStyle} labelFormatter={(value) => formatTrendBucketLabel(value, windowLabel, value)} />
                <Legend {...commonLegendProps} />
                <Bar dataKey="positive" stackId="feedback" fill="#10b981" name="Thumbs up" />
                <Bar dataKey="negative" stackId="feedback" fill="#ef4444" name="Thumbs down" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        </>
        )}
      </CardContent>
    </Card>
  );
}

function TopicDemandTrendCard({ trend, topics = [], windowLabel, controls, loading = false }) {
  const days = trend;
  const total = days.reduce((sum, day) => sum + Number(day.volume || day.count || day.value || 0), 0);
  const average = days.length > 0 ? Math.round(total / days.length) : 0;
  const peak = days.reduce((best, day) => {
    const value = Number(day.volume || day.count || day.value || 0);
    return value > best.value ? { value, date: day.date } : best;
  }, { value: 0, date: null });
  const topicSeries = topics.slice(0, 5).map((topic, index) => ({
    key: `topic_${index}`,
    id: String(topic.topic_id || topic.id || topic.topic || topic.name || index),
    label: topic.topic || topic.name || `Topic ${index + 1}`,
    color: topic.color || topic.topic_color || TOPIC_SERIES_COLORS[index % TOPIC_SERIES_COLORS.length],
  }));
  const chartData = days.map((day, idx) => {
    const row = {
      ...day,
      bucketLabel: formatTrendBucketLabel(day.date, windowLabel, idx + 1),
      aggregate: Number(day.volume || day.count || day.value || 0),
    };
    topicSeries.forEach((series) => {
      row[series.key] = Number(day.topics?.[series.id]?.volume || day.topics?.[series.id] || 0);
    });
    return row;
  });
  const hasTopicBreakdown = topicSeries.some((series) => chartData.some((row) => Number(row[series.key] || 0) > 0));

  return (
    <Card className="card-elevated border-0" data-testid="overview-topic-demand-trend-card">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-success" />
              Topic Demand Over Time
            </CardTitle>
            <p className="text-sm text-muted-foreground">{windowLabel === 'daily' ? 'Hourly' : 'Daily'} topic-classified question volume over the {windowLabel}</p>
          </div>
          {controls}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="space-y-5" data-testid="overview-topic-trend-loading">
            <div className="grid grid-cols-3 gap-3">
              <Skeleton className="h-[74px] rounded-xl" />
              <Skeleton className="h-[74px] rounded-xl" />
              <Skeleton className="h-[74px] rounded-xl" />
            </div>
            <Skeleton className="h-72 w-full rounded-xl" />
          </div>
        ) : (
        <>
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-xl bg-secondary/50 p-3">
            <p className="text-xs text-muted-foreground">Period total</p>
            <p className="text-xl font-semibold text-foreground">{formatCompactNumber(total)}</p>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3">
            <p className="text-xs text-muted-foreground">{windowLabel === 'daily' ? 'Hourly avg' : 'Daily avg'}</p>
            <p className="text-xl font-semibold text-foreground">{formatCompactNumber(average)}</p>
          </div>
          <div className="rounded-xl bg-secondary/50 p-3">
            <p className="text-xs text-muted-foreground">{windowLabel === 'daily' ? 'Peak hour' : 'Peak day'}</p>
            <p className="text-xl font-semibold text-foreground">{formatCompactNumber(peak.value)}</p>
          </div>
        </div>

        {days.length === 0 ? (
          <EmptyState
            icon={TrendingUp}
            title="No topic trend yet"
            description="Topic demand will appear as conversations are classified."
            variant="panel"
            className="border-0 bg-secondary/40"
          />
        ) : (
          <div className="h-72 rounded-xl bg-secondary/30 p-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }} barCategoryGap="30%" maxBarSize={36}>
                <CartesianGrid {...commonCartesianGridProps} />
                <XAxis
                  dataKey="bucketLabel"
                  {...commonXAxisProps}
                  interval={getTrendXAxisInterval(windowLabel)}
                />
                <YAxis {...commonYAxisProps} tickFormatter={formatCompactNumber} allowDecimals={false} width={36} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Legend {...commonLegendProps} />
                {hasTopicBreakdown ? (
                  topicSeries.map((series) => (
                    <Bar
                      key={series.key}
                      dataKey={series.key}
                      stackId="topics"
                      fill={series.color}
                      name={series.label}
                    />
                  ))
                ) : (
                  <Bar dataKey="aggregate" fill="#10b981" name="Topic-classified questions" radius={[6, 6, 0, 0]} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        </>
        )}
      </CardContent>
    </Card>
  );
}

function TopicMomentumCard({ topics, canManageTopics }) {
  const maxQueries = Math.max(...topics.slice(0, 5).map((topic) => Number(topic.query_count || 0)), 1);

  return (
    <Card className="card-elevated border-0" data-testid="overview-trending-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base sm:text-lg font-semibold text-foreground flex items-center gap-2">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-success" />
              Topic Momentum
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">Topics by query volume</p>
          </div>
          {canManageTopics && (
            <NavLink to="/kb/topics" className="text-xs sm:text-sm text-primary hover:underline">
              Manage
            </NavLink>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {topics.length === 0 ? (
          <EmptyState
            icon={TrendingUp}
            title="No topics yet"
            description="Top topics will appear once employees ask questions."
            variant="panel"
            className="border-0 bg-secondary/50 dark:bg-secondary/30"
          />
        ) : TOP_FIVE_SLOTS.map((idx) => {
          const topic = topics[idx];
          const TopicIcon = getTopicIconComponent(topic?.icon);
          const width = topic ? Math.max((Number(topic.query_count || 0) / maxQueries) * 100, 6) : 0;
          return (
            <div key={topic?.name || topic?.id || idx} className="space-y-2 rounded-xl bg-secondary/50 p-3 dark:bg-secondary/30">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <span
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background text-muted-foreground"
                    style={{ color: topic?.color || undefined }}
                  >
                    <TopicIcon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{topic?.name || `#${idx + 1}`}</p>
                    <p className="text-xs text-muted-foreground">
                      {topic ? `${topic.document_count || 0} docs` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">{topic ? formatCompactNumber(topic.query_count) : '-'}</span>
                  {topic?.trend === 'up' && <TrendingUp className="h-4 w-4 text-success" />}
                  {topic?.trend === 'down' && <ArrowDownRight className="h-4 w-4 text-destructive" />}
                  {topic?.trend === 'stable' && <Minus className="h-4 w-4 text-muted-foreground" />}
                </div>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-background">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${width}%`, backgroundColor: topic?.color || 'hsl(var(--primary))' }}
                />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default function OverviewPage() {
  const { hasRole, user, tenant } = useAuth();
  const canManageConnectors = hasRole(['admin']);
  const canManageTopics = hasRole(['admin', 'approver']);
  const canViewUsageDetails = hasRole(['admin', 'approver', 'analyst']);
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [conversationData, setConversationData] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [kbData, setKbData] = useState(null);
  const [usageTrendWindow, setUsageTrendWindow] = useState('7d');
  const [feedbackTrendWindow, setFeedbackTrendWindow] = useState('7d');
  const [topicTrendWindow, setTopicTrendWindow] = useState('7d');
  const [mapWindow, setMapWindow] = useState('7d');
  const [trendUsageData, setTrendUsageData] = useState(null);
  const [trendConversationData, setTrendConversationData] = useState(null);
  const [mapUsageData, setMapUsageData] = useState(null);
  const [mapConversationData, setMapConversationData] = useState(null);
  const [trendFeedbackData, setTrendFeedbackData] = useState(null);
  const [topicTrendData, setTopicTrendData] = useState(null);
  const [usageTrendLoading, setUsageTrendLoading] = useState(true);
  const [feedbackTrendLoading, setFeedbackTrendLoading] = useState(true);
  const [topicTrendLoading, setTopicTrendLoading] = useState(canViewUsageDetails);
  const [mapLoading, setMapLoading] = useState(false);
  const [connectorsData, setConnectorsData] = useState([]);
  const [tokenAlerts, setTokenAlerts] = useState([]);
  const [actionItems, setActionItems] = useState([]);
  const [tenantSettings, setTenantSettings] = useState(null);
  const [usageLeaders, setUsageLeaders] = useState({ dau: [], wau: [], mau: [] });
  const [gamificationLeaders, setGamificationLeaders] = useState([]);
  const [pulseGoal, setPulseGoal] = useState(getPulseGoal);
  const [pulseMessages, setPulseMessages] = useState([]);
  const [pulseDraft, setPulseDraft] = useState('');
  const pulseStreamTimerRef = useRef(null);
  const pulseConversationRef = useRef(null);
  const pulseHydratedRef = useRef(false);
  const pulseStorageKey = getPulseMessagesStorageKey(user, tenant);

  const streamPulseReply = (message, options = {}) => {
    if (pulseStreamTimerRef.current) {
      clearInterval(pulseStreamTimerRef.current);
    }

    const id = `eva-${Date.now()}`;
    setPulseMessages((current) => [...current, { id, role: 'eva', text: '', streaming: true, ...options }]);

    let index = 0;
    pulseStreamTimerRef.current = setInterval(() => {
      index += 2;
      setPulseMessages((current) => current.map((entry) => (
        entry.id === id
          ? { ...entry, text: message.slice(0, index), streaming: index < message.length }
          : entry
      )));

      if (index >= message.length) {
        clearInterval(pulseStreamTimerRef.current);
        pulseStreamTimerRef.current = null;
      }
    }, 18);
  };

  const handlePulseGoalChange = (goalKey, recommendation) => {
    setPulseGoal(goalKey);
    if (typeof window !== 'undefined') {
      if (goalKey) {
        window.localStorage.setItem(PULSE_GOAL_STORAGE_KEY, goalKey);
      } else {
        window.localStorage.removeItem(PULSE_GOAL_STORAGE_KEY);
      }
    }

    if (!goalKey) {
      setPulseMessages([]);
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(pulseStorageKey);
      }
      return;
    }

    const goal = PULSE_GOALS.find((item) => item.key === goalKey);
    streamPulseReply(buildPulseGoalReply(goal, recommendation, pulseWorkspaceContext), recommendation ? {
      recommendation: {
        title: recommendation.title,
        link: recommendation.link || '/',
      },
      cta: {
        label: recommendation.ctaLabel || 'Open',
        link: recommendation.link || '/',
      },
    } : {});
  };

  useEffect(() => () => {
    if (pulseStreamTimerRef.current) {
      clearInterval(pulseStreamTimerRef.current);
    }
  }, []);

  useEffect(() => {
    pulseHydratedRef.current = false;
    setPulseMessages(readStoredPulseMessages(pulseStorageKey));
    window.setTimeout(() => {
      pulseHydratedRef.current = true;
    }, 0);
  }, [pulseStorageKey]);

  useEffect(() => {
    if (!pulseHydratedRef.current || typeof window === 'undefined') return;

    const messagesToStore = pulseMessages.slice(-24).map((message) => ({
      ...message,
      streaming: false,
    }));
    window.localStorage.setItem(pulseStorageKey, JSON.stringify(messagesToStore));
  }, [pulseMessages, pulseStorageKey]);

  // Fetch all data
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [
          overviewRes,
          usageRes,
          convRes,
          feedbackRes,
          kbRes,
          connectorsRes,
          tokenAlertsRes,
          actionItemsRes,
          dailyUsersRes,
          weeklyUsersRes,
          monthlyUsersRes,
          gamificationRes,
          tenantSettingsRes,
        ] = await Promise.all([
          api.get('/analytics/overview').catch(() => ({ data: null })),
          api.get(`/analytics/usage?tz=${BROWSER_TZ}`).catch(() => ({ data: null })),
          api.get(`/analytics/conversations?tz=${BROWSER_TZ}`).catch(() => ({ data: null })),
          api.get(`/analytics/feedback?tz=${BROWSER_TZ}`).catch(() => ({ data: null })),
          api.get('/analytics/knowledge-base').catch(() => ({ data: null })),
          canManageConnectors
            ? api.get('/connectors').catch(() => ({ data: { connectors: [] } }))
            : Promise.resolve({ data: { connectors: [] } }),
          canManageConnectors
            ? api.get('/usage/alerts').catch(() => ({ data: { alerts: [] } }))
            : Promise.resolve({ data: { alerts: [] } }),
          api.get('/analytics/action-items').catch(() => ({ data: { items: [] } })),
          canViewUsageDetails
            ? api.get('/usage/by-user?window=day').catch(() => ({ data: { by_user: [] } }))
            : Promise.resolve({ data: { by_user: [] } }),
          canViewUsageDetails
            ? api.get('/usage/by-user?window=week').catch(() => ({ data: { by_user: [] } }))
            : Promise.resolve({ data: { by_user: [] } }),
          canViewUsageDetails
            ? api.get('/usage/by-user?window=period').catch(() => ({ data: { by_user: [] } }))
            : Promise.resolve({ data: { by_user: [] } }),
          api.get('/gamification/leaderboard?limit=5').catch(() => ({ data: { leaderboard: [] } })),
          api.get('/settings/tenant').catch(() => ({ data: {} })),
        ]);

        setOverviewData(overviewRes.data);
        setUsageData(usageRes.data);
        setConversationData(convRes.data);
        setFeedbackData(feedbackRes.data);
        setKbData(kbRes.data);
        setConnectorsData(connectorsRes.data?.connectors || []);
        setTokenAlerts(tokenAlertsRes.data?.alerts || []);
        setActionItems(actionItemsRes.data?.items || []);
        setTenantSettings(tenantSettingsRes.data || {});
        // Usage Leaders is an Eva People engagement board, so it only counts
        // conversation-bucket activity (chat, research, etc.) — a user whose
        // only activity is Eva Insights knowledge-base work (document
        // ingest/analysis) has nothing to show here. That keeps this card's
        // "credits" figure reconciled with each employee's own Eva People
        // Credits meter (/usage/me/capacity), which is conversation-only too.
        const conversationUsersOnly = (users) => users.filter(
          (u) => Number(u.conversation_request_count || 0) > 0 || Number(u.conversation_tokens || 0) > 0
        );
        const rankUsageUsers = (users) => [...users]
          .sort((a, b) => Number(b.conversation_tokens || 0) - Number(a.conversation_tokens || 0)
            || Number(b.conversation_request_count || 0) - Number(a.conversation_request_count || 0))
          .slice(0, 5);
        const dailyUsers = conversationUsersOnly(dailyUsersRes.data?.by_user || []);
        const weeklyUsers = conversationUsersOnly(weeklyUsersRes.data?.by_user || []);
        const monthlyUsers = conversationUsersOnly(monthlyUsersRes.data?.by_user || []);

        setUsageLeaders({
          dau: {
            count: dailyUsers.length,
            rows: rankUsageUsers(dailyUsers),
          },
          wau: {
            count: weeklyUsers.length,
            rows: rankUsageUsers(weeklyUsers),
          },
          mau: {
            count: monthlyUsers.length,
            rows: rankUsageUsers(monthlyUsers),
          },
        });
        setGamificationLeaders((gamificationRes.data?.leaderboard || []).slice(0, 5));
      } catch (err) {
        console.error('Failed to fetch data:', err);
      }
      setLoading(false);
    };
    fetchAllData();
  }, [canManageConnectors, canViewUsageDetails]);

  useEffect(() => {
    let cancelled = false;
    const fetchUsageTrendData = async () => {
      setUsageTrendLoading(true);
      const [trendUsageRes, trendConversationRes] = await Promise.all([
        api.get(`/analytics/usage?window=${usageTrendWindow}&tz=${BROWSER_TZ}`).catch(() => ({ data: null })),
        api.get(`/analytics/conversations?window=${usageTrendWindow}&tz=${BROWSER_TZ}`).catch(() => ({ data: null })),
      ]);
      if (cancelled) return;

      setTrendUsageData(trendUsageRes.data);
      setTrendConversationData(trendConversationRes.data);
      setUsageTrendLoading(false);
    };

    fetchUsageTrendData();
    return () => {
      cancelled = true;
    };
  }, [usageTrendWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchMapData = async () => {
      if (activeTab !== 'world') return;

      setMapLoading(true);
      const [usageMapRes, conversationMapRes] = await Promise.all([
        api.get(`/analytics/usage?window=${mapWindow}&tz=${BROWSER_TZ}`).catch(() => ({ data: null })),
        api.get(`/analytics/conversations?window=${mapWindow}&tz=${BROWSER_TZ}`).catch(() => ({ data: null })),
      ]);
      if (cancelled) return;

      setMapUsageData(usageMapRes.data);
      setMapConversationData(conversationMapRes.data);
      setMapLoading(false);
    };

    fetchMapData();
    return () => {
      cancelled = true;
    };
  }, [activeTab, mapWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchFeedbackTrendData = async () => {
      setFeedbackTrendLoading(true);
      const trendFeedbackRes = await api
        .get(`/analytics/feedback?window=${feedbackTrendWindow}&tz=${BROWSER_TZ}`)
        .catch(() => ({ data: null }));
      if (cancelled) return;

      setTrendFeedbackData(trendFeedbackRes.data);
      setFeedbackTrendLoading(false);
    };

    fetchFeedbackTrendData();
    return () => {
      cancelled = true;
    };
  }, [feedbackTrendWindow]);

  useEffect(() => {
    let cancelled = false;
    const fetchTopicTrendData = async () => {
      if (canViewUsageDetails) setTopicTrendLoading(true);
      const topicTrendRes = canViewUsageDetails
        ? await api
          .get(`/analytics/trending-topics?window=${topicTrendWindow}&tz=${BROWSER_TZ}`)
          .catch(() => ({ data: null }))
        : { data: null };
      if (cancelled) return;

      setTopicTrendData(topicTrendRes.data);
      setTopicTrendLoading(false);
    };

    fetchTopicTrendData();
    return () => {
      cancelled = true;
    };
  }, [canViewUsageDetails, topicTrendWindow]);

  // Build KPIs from real data
  const usageSnapshot = usageData?.executive_snapshot || {};
  const convSnapshot = conversationData?.executive_snapshot || {};
  const fbSnapshot = feedbackData?.executive_snapshot || {};
  const kbSnapshot = kbData?.executive_snapshot || {};
  const overviewStats = overviewData?.stats || {};

  // Calculate KB coverage: ratio of indexed documents to total documents
  const totalDocs = overviewStats.total_documents || 0;
  const indexedDocs = kbSnapshot.total_documents || 0;
  const kbCoverage = totalDocs > 0 ? Math.round((indexedDocs / totalDocs) * 100) : 0;
  const getTrendChange = (trendArray, valueKey) => {
    if (!Array.isArray(trendArray) || trendArray.length < 2) return 0;
    const midpoint = Math.floor(trendArray.length / 2);
    const previous = trendArray
      .slice(0, midpoint)
      .reduce((sum, item) => sum + Number(item[valueKey] || item.value || item.count || 0), 0);
    const current = trendArray
      .slice(midpoint)
      .reduce((sum, item) => sum + Number(item[valueKey] || item.value || item.count || 0), 0);
    if (previous <= 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };
  const usageTrendChange = getTrendChange(usageData?.trend, 'active_users');
  const conversationTrendChange = Number(conversationData?.trend_percent ?? getTrendChange(conversationData?.trend_data, 'conversations'));
  const sentimentTrendChange = getTrendChange(feedbackData?.trend, 'positive');
  const activeUsersThisWeek = Number(
    usageSnapshot.active_users_7d ?? usageSnapshot.wau ?? usageSnapshot.weekly_active_users ?? usageSnapshot.active_users_30d ?? 0
  );

  const kpis = [
    {
      title: 'Employees using\nEva this week',
      value: activeUsersThisWeek.toLocaleString(),
      change: usageTrendChange,
      icon: Users,
      color: 'purple',
      link: '/people/usage',
      subtitle: `${formatCompactNumber(usageSnapshot.dau || 0)} today • ${formatCompactNumber(usageSnapshot.mau || usageSnapshot.active_users_30d || 0)} this month`,
    },
    {
      title: 'Questions answered\nby Eva',
      value: convSnapshot.total_conversations?.toLocaleString() || '0',
      change: conversationTrendChange,
      icon: MessageSquare,
      color: 'info',
      link: '/conversations/activity',
      subtitle: `${formatCompactNumber(convSnapshot.resolved_conversations || 0)} resolved • ${Math.round(convSnapshot.retrieval_rate || 0)}% used retrieval`,
    },
    {
      title: 'Helpful answers\nby Eva',
      value: `${Math.round(fbSnapshot.positive_pct || 0)}%`,
      change: sentimentTrendChange,
      icon: ThumbsUp,
      color: 'emerald',
      link: '/conversations/feedback',
      subtitle: `${formatCompactNumber(fbSnapshot.positive_count || 0)} marked helpful • ${formatCompactNumber(fbSnapshot.negative_count || 0)} to review`,
    },
    {
      title: 'Knowledge Base\nCoverage',
      value: `${kbCoverage}%`,
      change: 0,
      icon: BookOpen,
      color: 'amber',
      link: '/kb/coverage',
      subtitle: `${formatCompactNumber(indexedDocs)} docs ready • ${formatCompactNumber(overviewStats.content_gaps_count || 0)} new gaps`,
    },
  ];

  // Build alerts from real data
  const contentGapsCount = overviewData?.stats?.content_gaps_count || 0;
  const publishedDocumentsCount = Number(
    overviewData?.stats?.published_documents ?? overviewData?.stats?.total_documents ?? 0
  );
  const negativeFeedback = fbSnapshot.negative_count || 0;
  const trendingTopicsData = overviewData?.trending_topics || [];
  const recentFeedback = (feedbackData?.responses || []).slice(0, 5);
  const usageTrendWindowLabel = getTrendRangeLabel(usageTrendWindow).toLowerCase();
  const feedbackTrendWindowLabel = getTrendRangeLabel(feedbackTrendWindow).toLowerCase();
  const topicTrendWindowLabel = getTrendRangeLabel(topicTrendWindow).toLowerCase();
  const trendUsage = trendUsageData || usageData || {};
  const trendConversations = trendConversationData || conversationData || {};
  const trendFeedback = trendFeedbackData || feedbackData || {};

  const alerts = [];
  actionItems.forEach((item) => {
    const isTokenCapacity = item.resource === 'tokens' || item.area === 'Token Usage';
    alerts.push({
      title: getPulseTitle(item),
      description: isTokenCapacity ? formatTokenCapacityDescription(item) : item.description,
      severity: item.severity,
      link: item.cta_url || '/settings/billing',
      ctaLabel: item.cta_label || 'View',
      category: getPulseCategory(item),
      roles: item.roles || (item.resource === 'tokens' || item.area === 'Billing' ? ['admin'] : ['admin', 'approver', 'analyst']),
      resource: item.resource,
      usage: item.usage,
      limit: item.limit,
      percent_used: item.percent_used,
    });
  });
  if (publishedDocumentsCount > 0 && contentGapsCount > 0) {
    alerts.push({
      title: 'Fill Knowledge Gaps',
      description: `${contentGapsCount} new content gap${contentGapsCount > 1 ? 's' : ''} identified from user queries`,
      severity: 'high',
      link: '/kb/gaps',
      category: 'Knowledge Base',
      roles: ['admin', 'approver', 'analyst'],
    });
  }
  if (negativeFeedback > 0) {
    alerts.push({
      title: 'Review Feedback',
      description: `${negativeFeedback} negative rating${negativeFeedback > 1 ? 's' : ''} in the selected period`,
      severity: negativeFeedback > 5 ? 'high' : 'medium',
      link: '/conversations/feedback',
      category: 'Conversations',
      roles: ['admin', 'approver', 'analyst', 'viewer'],
    });
  }
  if (trendingTopicsData.length > 0 && trendingTopicsData[0].trend === 'up') {
    alerts.push({
      title: 'Explore Rising Topic',
      description: `"${trendingTopicsData[0].name}" is trending with ${trendingTopicsData[0].query_count} queries`,
      severity: 'low',
      link: '/conversations/trending',
      category: 'Conversations',
      roles: ['admin', 'approver', 'analyst', 'viewer'],
    });
  }
  const enabledUsers = Number(usageSnapshot.users_enabled || usageSnapshot.total_users || 0);
  const activeUsers30d = Number(usageSnapshot.active_users_30d || 0);
  if (enabledUsers > 0 && activeUsers30d / enabledUsers < 0.25) {
    alerts.push({
      title: 'Lift employee adoption',
      description: `${activeUsers30d.toLocaleString()} of ${enabledUsers.toLocaleString()} enabled employees used Eva in the last 30 days.`,
      severity: activeUsers30d === 0 ? 'high' : 'medium',
      link: '/people/adoption',
      category: 'People',
      roles: ['admin', 'approver', 'analyst'],
    });
  }
  if (gamificationLeaders.length === 0 && enabledUsers > 0) {
    alerts.push({
      title: 'Kickstart engagement',
      description: 'No leaderboard activity yet. A focused notification can help employees try Eva.',
      severity: 'low',
      link: '/engagement/notifications',
      category: 'Engagement',
      roles: ['admin'],
    });
  }
  const hasTokenActionItem = actionItems.some((item) => item.resource === 'tokens');
  if (!hasTokenActionItem) tokenAlerts.forEach((alert) => {
    alerts.push({
      title: 'Manage AI credits',
      description: formatTokenCapacityDescription({ ...alert, percent_used: alert.current_usage_pct }),
      severity: Number(alert.current_usage_pct || alert.threshold_pct || 0) >= 95 ? 'high' : 'medium',
      link: '/settings/usage',
      category: 'AI Usage',
      roles: ['admin'],
    });
  });
  const activeConnectorStatuses = new Set(['connected', 'enabled', 'active']);
  connectorsData
    .filter((connector) => {
      const status = String(connector.status || '').toLowerCase();
      const syncStatus = String(connector.sync_status || '').toLowerCase();
      const hasSyncIssue = syncStatus === 'error' || syncStatus === 'failed' || Boolean(connector.sync_error);
      return activeConnectorStatuses.has(status) && hasSyncIssue && (connector.type_name || connector.name);
    })
    .forEach((connector) => {
      alerts.push({
        title: 'Reconnect Data Source',
        description: `${connector.type_name || connector.name} needs attention`,
        severity: 'high',
        link: '/connectors',
        category: 'Connectors',
        roles: ['admin'],
      });
    });
  const selectedPulseGoal = PULSE_GOALS.find((goal) => goal.key === pulseGoal);
  const visibleAlerts = sortPulseAlerts(
    alerts.filter((alert) => !alert.roles || hasRole(alert.roles)),
    pulseGoal
  );
  const pulseSignals = groupPulseAlerts(visibleAlerts);
  const attentionCount = pulseSignals.length;
  const primaryPulseAlert = pulseSignals[0];
  const pulseWorkspaceContext = {
    user,
    tenant,
    alerts: pulseSignals,
    snapshots: {
      usage: usageSnapshot,
      conversations: convSnapshot,
      feedback: fbSnapshot,
      knowledgeBase: kbSnapshot,
      overview: overviewStats,
    },
  };
  const smartEvaPulseEnabled = tenantSettings?.smart_eva_pulse_enabled === true
    || tenantSettings?.smart_eva_pulse_enabled === 1;
  const pulseIntro = attentionCount > 0
    ? selectedPulseGoal
      ? `I’m evaluating the workspace to optimize ${selectedPulseGoal.label.toLowerCase()}. I’d start with ${primaryPulseAlert?.title?.toLowerCase()} based on the current signals.`
      : `I found ${attentionCount} open ${attentionCount === 1 ? 'signal' : 'signals'}. Tell me what you want to optimize for and I’ll prioritize the next steps.`
    : selectedPulseGoal
      ? `I’m evaluating the tenant workspace for ${selectedPulseGoal.label.toLowerCase()}. When new warnings or actions appear, I’ll track them here and recommend the clearest next step.`
      : 'Tell me what your HR team wants to improve, and I’ll evaluate the tenant workspace for the most useful recommendations.';
  const handlePulseSubmit = (event) => {
    event.preventDefault();
    const message = pulseDraft.trim();
    if (!message) return;

    setPulseDraft('');
    setPulseMessages((current) => [...current, { id: `user-${Date.now()}`, role: 'user', text: message }]);
    streamPulseReply(buildPulseFollowUpReply(message, selectedPulseGoal, primaryPulseAlert, pulseWorkspaceContext), primaryPulseAlert ? {
      recommendation: {
        title: primaryPulseAlert.title,
        link: primaryPulseAlert.link || '/',
      },
      cta: {
        label: primaryPulseAlert.ctaLabel || 'Open',
        link: primaryPulseAlert.link || '/',
      },
    } : {});
  };

  const handlePulseAsk = (alert) => {
    setPulseMessages((current) => [
      ...current,
      { id: `user-${Date.now()}`, role: 'user', text: `What should we do about “${alert.title}”?` },
    ]);
    streamPulseReply(buildPulseAlertReply(alert, selectedPulseGoal, pulseWorkspaceContext), {
      recommendation: {
        title: alert.title,
        link: alert.link || '/',
      },
      cta: {
        label: alert.ctaLabel || 'Open',
        link: alert.link || '/',
      },
    });
  };

  useEffect(() => {
    const scrollToLatest = () => {
      const node = pulseConversationRef.current;
      if (!node) return;
      if (typeof node.scrollTo === 'function') {
        node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
      } else {
        node.scrollTop = node.scrollHeight;
      }
    };

    scrollToLatest();
    const restoreScrollTimer = window.setTimeout(scrollToLatest, 180);
    return () => window.clearTimeout(restoreScrollTimer);
  }, [pulseMessages, attentionCount]);

  // Build regions from real data
  const mapUsageSource = mapUsageData || usageData;
  const mapConversationSource = mapConversationData || conversationData;
  const regionalData = (mapUsageSource?.regional_performance?.length
    ? mapUsageSource.regional_performance
    : mapConversationSource?.regional_breakdown) || [];
  const countryData = (mapUsageSource?.country_highlights?.length
    ? mapUsageSource.country_highlights
    : mapConversationSource?.country_breakdown) || [];
  const regions = regionalData.map((r, _idx) => ({
    region: r.region === 'NA' ? 'North America' :
            r.region === 'EMEA' ? 'EMEA' :
            r.region === 'APAC' ? 'APAC' :
            r.region === 'LATAM' ? 'LATAM' : r.region,
    users: r.active_users || 0,
    conversations: r.signins || r.conversations || 0,
  }));
  const countries = countryData.map((country) => ({
    country: country.country,
    region: country.region,
    users: country.active_users || 0,
    conversations: country.conversations || 0,
  }));

  const { startTour } = usePageTour();
  const { hasBeenSeen: hasSeenWelcome } = useWelcomeGraphic('overview');

  // Check if this is a new tenant with no data
  const hasAnyData = usageData?.has_data || conversationData?.has_data ||
                     (usageSnapshot.users_enabled > 0) || (convSnapshot.total_conversations > 0);

  // Skeleton loader while loading
  if (loading) {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-6 animate-fade-in" data-testid="overview-page-loading">
        <Skeleton className="h-12 w-full rounded-xl" />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-9 w-44" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-5 w-40" />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="card-elevated border-0">
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <Skeleton className="h-12 w-12 shrink-0 rounded-2xl" />
                  <div className="min-w-0 flex-1 space-y-3">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-9 w-20" />
                    <Skeleton className="h-4 w-36" />
                  </div>
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-black/5 pt-4 dark:border-white/10">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-4 w-12" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="card-elevated overflow-hidden border-0">
          <CardContent className="p-5 sm:p-6">
            <div className="flex gap-4">
              <Skeleton className="h-16 w-16 shrink-0 rounded-2xl" />
              <div className="min-w-0 flex-1 space-y-4">
                <Skeleton className="h-7 w-36" />
                <Skeleton className="h-20 w-full rounded-2xl" />
                <div className="space-y-3 rounded-2xl border border-border p-4">
                  <Skeleton className="h-4 w-32" />
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-16 w-full rounded-xl" />
                  ))}
                </div>
                <Skeleton className="h-12 w-full rounded-2xl" />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="rounded-2xl border border-border bg-card p-2 shadow-sm">
          <div className="grid grid-cols-3 gap-2">
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
            <Skeleton className="h-12 rounded-xl" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((section) => (
            <Card key={section} className="card-elevated border-0">
              <CardHeader className="pb-3">
                <Skeleton className="h-6 w-44" />
                <Skeleton className="h-4 w-56" />
              </CardHeader>
              <CardContent className="space-y-3">
                {[1, 2, 3, 4, 5].map((row) => (
                  <div key={row} className="flex items-center gap-3 rounded-xl bg-muted/35 p-3">
                    <Skeleton className="h-9 w-9 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-28" />
                    </div>
                    <Skeleton className="h-6 w-16 rounded-full" />
                  </div>
                ))}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 animate-fade-in" data-testid="overview-page">
      {/* Welcome Graphic - Shows once for new users */}
      {!hasSeenWelcome && (
        <WelcomeGraphic
          {...welcomeConfigs.overview}
          ctaText="Take a Tour"
          onCtaClick={() => startTour('overview')}
          variant="default"
          className="mb-2"
        />
      )}

      <EvaPeopleActivityTicker className="rounded-xl border border-border" />

      <PageHeader
        title="Overview"
        description={
          hasAnyData
            ? 'Executive snapshot of Eva performance'
            : 'See how employees are using Eva, where they need help, and what needs attention.'
        }
        actions={(
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
            {hasSeenWelcome && (
              <Button
                onClick={() => startTour('overview')}
                variant="outline"
                size="sm"
                className="group whitespace-nowrap border-primary/30 text-primary hover:bg-primary/10 hover:border-primary/50"
                data-testid="take-tour-overview"
              >
                <Play className="w-3.5 h-3.5 mr-1.5 fill-current" />
                Take a Tour
              </Button>
            )}
            <div className="text-xs text-muted-foreground sm:text-sm">
              Last updated: {new Date().toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </div>
          </div>
        )}
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="overview-kpi-cards">
        {kpis.map((kpi) => (
          <NavLink key={kpi.title} to={kpi.link} className="block h-full">
            <KpiCard
              title={kpi.title}
              value={kpi.value}
              subtitle={kpi.subtitle}
              icon={kpi.icon}
              color={kpi.color}
              trend={kpi.change}
              className="h-full"
            />
          </NavLink>
        ))}
      </div>

      {smartEvaPulseEnabled ? (
        <Card
          className="card-elevated relative overflow-hidden border-0 bg-card before:pointer-events-none before:absolute before:inset-0 before:bg-[conic-gradient(from_0deg,hsl(var(--primary)/0.55),hsl(var(--accent)/0.35),transparent_35%,transparent_65%,hsl(var(--primary)/0.55))] before:animate-spin before:[animation-duration:10s] after:pointer-events-none after:absolute after:inset-[2px] after:rounded-[calc(var(--radius)-2px)] after:bg-card"
          data-testid="overview-alerts-card"
        >
        <CardHeader className="relative z-10 pb-3">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex gap-3">
              <EvaSpeakingAvatar active={attentionCount > 0 || !pulseGoal} />
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-xl font-semibold text-foreground">
                    Eva Pulse
                  </CardTitle>
                </div>
                <p className="mt-2 max-w-3xl rounded-2xl rounded-tl-sm border border-border/70 bg-background/70 px-4 py-3 text-sm leading-6 text-muted-foreground shadow-sm">
                  {pulseIntro}
                </p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="relative z-10 flex min-h-[420px] flex-col gap-3">
          {!pulseGoal && (
            <div className="rounded-2xl border border-dashed border-primary/25 bg-primary/5 p-4">
              <p className="text-sm font-semibold text-foreground">What should I help your HR team with first?</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {PULSE_GOALS.map((goal) => (
                  <Button
                    key={goal.key}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-xl bg-background/80"
                    onClick={() => handlePulseGoalChange(goal.key, primaryPulseAlert)}
                  >
                    {goal.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
          {pulseGoal && (
            <PulseGoalAnchor goal={selectedPulseGoal} onChange={() => handlePulseGoalChange('')} />
          )}
          <div
            ref={pulseConversationRef}
            className="min-h-0 flex-1 space-y-3 overflow-y-auto rounded-2xl border border-border bg-background/60 p-4"
            data-testid="eva-pulse-conversation"
          >
            <PulseMessage
              message={{
                id: 'pulse-anchor-message',
                role: 'eva',
                text: pulseGoal
                  ? `I’ll evaluate profile, tenant, activity, limits, and open alerts against this objective. Ask about any signal and I’ll explain why it matters and what to do next.`
                  : `Pick a goal and I’ll evaluate the workspace around what your HR team wants to improve.`,
              }}
            />
            <div className="relative ml-5 border-l border-dashed border-border/80 pl-5">
              <span className="absolute -left-1.5 top-0 h-3 w-3 rounded-full bg-primary shadow-sm shadow-primary/40" aria-hidden="true" />
              <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Workspace signals
              </p>
              {pulseSignals.length > 0 ? (
                <div className="space-y-3">
                  {pulseSignals.slice(0, 5).map((alert, idx) => (
                    <PulseSignalCard
                      key={`${alert.title}-${idx}`}
                      alert={alert}
                      index={idx}
                      onAsk={handlePulseAsk}
                      featured={idx === 0}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-border bg-card/60 p-4 text-sm leading-6 text-muted-foreground">
                  New warnings and action items will appear here as Eva finds them. Ask me what to optimize for, and I’ll prioritize the next useful step.
                </div>
              )}
            </div>
            {pulseMessages.length > 0 && pulseMessages.map((message) => (
              <PulseMessage key={message.id} message={message} />
            ))}
          </div>
          <form className="sticky bottom-0 flex flex-col gap-2 rounded-2xl border border-border bg-background/95 p-2 shadow-sm backdrop-blur sm:flex-row" onSubmit={handlePulseSubmit}>
            <input
              value={pulseDraft}
              onChange={(event) => setPulseDraft(event.target.value)}
              placeholder="Ask Eva about an alert or tell her what to optimize for..."
              className="min-h-10 flex-1 rounded-xl bg-transparent px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground"
              aria-label="Reply to Eva Pulse"
            />
            <Button type="submit" size="sm" className="h-10 rounded-xl px-4">
              <Send className="mr-2 h-4 w-4" />
              Send
            </Button>
          </form>
        </CardContent>
        </Card>
      ) : (
        <ActionListCard alerts={pulseSignals} />
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
        <AnalyticsTabBar />

        <TabsContent value="details" className="space-y-6">
          <GamificationLeaderboardCard leaders={gamificationLeaders} />

          {canViewUsageDetails && <UsageLeadersCard leaders={usageLeaders} />}

          <RecentFeedbackCard feedback={recentFeedback} />

          <TopicMomentumCard topics={overviewData?.trending_topics || []} canManageTopics={canManageTopics} />
        </TabsContent>

        <TabsContent value="trends" className="space-y-6">
          <WeeklyActivityCard
            trend={trendConversations?.trend_data || trendUsage?.trend || []}
            windowLabel={usageTrendWindowLabel}
            controls={<TrendRangeTabs value={usageTrendWindow} onChange={setUsageTrendWindow} />}
            loading={usageTrendLoading}
          />
          <FeedbackMixCard
            trend={trendFeedback?.trend || []}
            snapshot={trendFeedback?.executive_snapshot || fbSnapshot}
            windowLabel={feedbackTrendWindowLabel}
            controls={<TrendRangeTabs value={feedbackTrendWindow} onChange={setFeedbackTrendWindow} />}
            loading={feedbackTrendLoading}
          />
          <TopicDemandTrendCard
            trend={topicTrendData?.volume_trend || []}
            topics={topicTrendData?.trending_topics || topicTrendData?.trending || []}
            windowLabel={topicTrendWindowLabel}
            controls={<TrendRangeTabs value={topicTrendWindow} onChange={setTopicTrendWindow} />}
            loading={topicTrendLoading}
          />
        </TabsContent>

        <TabsContent value="world" className="space-y-6">
          <RegionalMapView
            title="Global Adoption Map"
            description="Regional adoption with conversation volume"
            regions={regions.map((region) => ({
              region: region.region,
              value: region.users,
              secondaryValue: region.conversations,
            }))}
            countries={countries.map((country) => ({
              country: country.country,
              region: country.region,
              value: country.users,
              secondaryValue: country.conversations,
            }))}
            metricLabel="Users"
            secondaryLabel="Conversations"
            colorScale="green"
            emptyMessage="Regional adoption signals will appear as employees interact with Eva."
            timeRangeValue={mapWindow}
            onTimeRangeChange={setMapWindow}
            loading={mapLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
